import { useState, useEffect } from 'react';
import { Image as ImageIcon, X, Download } from 'lucide-react';

const API_BASE = process.env.REACT_APP_BACKEND_URL || 'http://localhost:8001';

/**
 * Secure inline image attachment viewer.
 * Fetches image with auth token (no backend URL exposed in browser).
 * Props:
 *   attachments: [{id, original_name, ...}]
 *   tokenHeader: { 'Authorization': 'Bearer ...' }
 *   endpoint: 'tickets' | 'support' (student vs agent)
 *   theme: 'light' | 'dark'
 */
export function AttachmentViewer({ attachments, tokenHeader, endpoint = 'tickets', theme = 'light' }) {
  const [preview, setPreview] = useState(null);

  if (!attachments || attachments.length === 0) return null;

  const apiPath = endpoint === 'support'
    ? `${API_BASE}/api/support/attachment`
    : `${API_BASE}/api/tickets/attachment`;

  return (
    <>
      <div className="mt-2 flex flex-wrap gap-2">
        {attachments.map((att, i) => (
          <AttachmentThumb
            key={i}
            att={att}
            apiPath={apiPath}
            tokenHeader={tokenHeader}
            theme={theme}
            onPreview={() => setPreview(att)}
          />
        ))}
      </div>

      {/* Full-size preview modal */}
      {preview && (
        <div className="fixed inset-0 bg-black/80 z-50 flex items-center justify-center p-4" onClick={() => setPreview(null)}>
          <div className="relative max-w-4xl max-h-[90vh]" onClick={e => e.stopPropagation()}>
            <button onClick={() => setPreview(null)}
              className="absolute -top-10 right-0 text-white hover:text-red-400 transition-colors">
              <X className="w-6 h-6" />
            </button>
            <PreviewImage att={preview} apiPath={apiPath} tokenHeader={tokenHeader} />
            <p className="text-center text-white/70 text-xs mt-2">{preview.original_name}</p>
          </div>
        </div>
      )}
    </>
  );
}

function AttachmentThumb({ att, apiPath, tokenHeader, theme, onPreview }) {
  const [blobUrl, setBlobUrl] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      try {
        const res = await fetch(`${apiPath}/${att.id}`, { headers: tokenHeader });
        if (!res.ok) throw new Error('Failed');
        const blob = await res.blob();
        if (!cancelled) setBlobUrl(URL.createObjectURL(blob));
      } catch {
        if (!cancelled) setError(true);
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    load();
    return () => { cancelled = true; };
  }, [att.id, apiPath, tokenHeader]);

  useEffect(() => {
    return () => { if (blobUrl) URL.revokeObjectURL(blobUrl); };
  }, [blobUrl]);

  const isDark = theme === 'dark';

  if (loading) {
    return (
      <div className={`w-20 h-20 rounded-lg flex items-center justify-center animate-pulse ${isDark ? 'bg-white/10' : 'bg-gray-100'}`}>
        <ImageIcon className={`w-4 h-4 ${isDark ? 'text-white/40' : 'text-gray-400'}`} />
      </div>
    );
  }

  if (error) {
    return (
      <div className={`flex items-center gap-1 text-xs px-2 py-1 rounded ${isDark ? 'text-red-400 bg-red-500/10' : 'text-red-500 bg-red-50'}`}>
        <ImageIcon className="w-3 h-3" /> Failed to load
      </div>
    );
  }

  return (
    <img
      src={blobUrl}
      alt={att.original_name}
      className="w-20 h-20 object-cover rounded-lg cursor-pointer border border-white/20 hover:opacity-80 transition-opacity"
      onClick={onPreview}
      title={att.original_name}
    />
  );
}

function PreviewImage({ att, apiPath, tokenHeader }) {
  const [blobUrl, setBlobUrl] = useState(null);

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      try {
        const res = await fetch(`${apiPath}/${att.id}`, { headers: tokenHeader });
        if (!res.ok) throw new Error('Failed');
        const blob = await res.blob();
        if (!cancelled) setBlobUrl(URL.createObjectURL(blob));
      } catch { /* ignore */ }
    };
    load();
    return () => { cancelled = true; };
  }, [att.id, apiPath, tokenHeader]);

  useEffect(() => {
    return () => { if (blobUrl) URL.revokeObjectURL(blobUrl); };
  }, [blobUrl]);

  if (!blobUrl) {
    return <div className="w-64 h-64 bg-white/10 rounded-lg flex items-center justify-center animate-pulse">
      <ImageIcon className="w-8 h-8 text-white/40" />
    </div>;
  }

  return <img src={blobUrl} alt={att.original_name} className="max-w-full max-h-[80vh] rounded-lg shadow-2xl" />;
}
